import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { showError } from "../../../notifications/notificationsSlice"
import { isDocEditable } from "../../documentSlice"
import {
    loadRiskAnalysisRpnLimits,
    updateRiskAnalysisRpnLimits,
    RiskAnalysisRpnLimits
} from "./../riskAnalysisApi"
import {
    Box,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material"
import { Localized } from "@fluent/react"
import LimitsEditDialog from "./../dialogs/LimitsEditDialog"
import { EditButton } from "../../../../app/components/EditButton"

type RiskAnalysisRpnLevel = keyof RiskAnalysisRpnLimits

const levelsOrder: RiskAnalysisRpnLevel[] = ["low", "medium", "high"]

const getDisplayedRange = (level: RiskAnalysisRpnLevel, limits: RiskAnalysisRpnLimits) => {
    const lowUpper = limits.low?.maxValue ?? limits.low?.minValue ?? 0
    const mediumUpper = limits.medium?.maxValue ?? Math.max(lowUpper, limits.medium?.minValue ?? 0)

    if (level === "low") {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                <Box sx={{ width: 28, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>1</Box>
                <Box sx={{ width: 20, textAlign: "center" }}>-</Box>
                <Box sx={{ width: 36, textAlign: "left", fontVariantNumeric: "tabular-nums" }}>{lowUpper}</Box>
            </Box>
        )
    }

    if (level === "medium") {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                <Box sx={{ width: 28, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{lowUpper + 1}</Box>
                <Box sx={{ width: 20, textAlign: "center" }}>-</Box>
                <Box sx={{ width: 36, textAlign: "left", fontVariantNumeric: "tabular-nums" }}>{mediumUpper}</Box>
            </Box>
        )
    }

    const highMin = limits.high?.minValue ?? 0
    const highStart = highMin > 0 ? highMin : mediumUpper + 1
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
            }}
        >
            <Box component="span">≥</Box>
            <Box component="span">{highStart}</Box>
        </Box>
    )
}

interface RiskAnalysisLimitsSectionProps {
    documentId: number
}

export const RiskAnalysisLimitsSection = ({ documentId }: RiskAnalysisLimitsSectionProps) => {
    const dispatch = useAppDispatch()
    const isEditable = useAppSelector(isDocEditable)
    const theme = useTheme()

    const levelStyles: Record<RiskAnalysisRpnLevel, { border: string, chipColor: "success" | "warning" | "error" }> = {
        low: {
            border: theme.palette.success.main,
            chipColor: "success",
        },
        medium: {
            border: theme.palette.warning.main,
            chipColor: "warning",
        },
        high: {
            border: theme.palette.error.main,
            chipColor: "error",
        },
    }

    // Items state
    const [items, setItems] = useState<RiskAnalysisRpnLimits | null>(null)
    const [loading, setLoading] = useState(false)

    // Edit dialog
    const [saving, setSaving] = useState(false)

    const [editDialogOpen, setEditDialogOpen] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const result = await dispatch(loadRiskAnalysisRpnLimits({ docId: documentId })).unwrap()
                setItems(result)
            } catch {
                dispatch(showError('error'))
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [dispatch, documentId])

    const handleOpenEditDialog = () => {
        setEditDialogOpen(true);
    };

    const handleSaveEditDialog = async (values: { lowMax: number; mediumMax: number; descriptions: { low: string; medium: string; high: string } }) => {
        if (!items) return;
        setSaving(true);
        try {
            const updatedLimits: RiskAnalysisRpnLimits = {
                low: {
                    id: items.low?.id ?? null,
                    minValue: 1,
                    maxValue: values.lowMax,
                    description: values.descriptions.low,
                    name: items.low?.name ?? "Low"
                },
                medium: {
                    id: items.medium?.id ?? null,
                    minValue: values.lowMax + 1,
                    maxValue: values.mediumMax,
                    description: values.descriptions.medium,
                    name: items.medium?.name ?? "Medium"
                },
                high: {
                    id: items.high?.id ?? null,
                    minValue: values.mediumMax + 1,
                    maxValue: items.high?.maxValue ?? null,
                    description: values.descriptions.high,
                    name: items.high?.name ?? "High"
                },
            };

            const updated = await dispatch(updateRiskAnalysisRpnLimits({
                documentId,
                limits: updatedLimits,
            })).unwrap();
            setItems(updated);
            setEditDialogOpen(false);
        } catch {
            dispatch(showError('error'));
        } finally {
            setSaving(false);
        }
    };

    return <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
            <Typography variant="h6">
                Limity
            </Typography>
            {isEditable && (
                <EditButton onClick={handleOpenEditDialog} loading={false} variant="outlined" />
            )}
        </Box>

        {loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
            : <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }}><Localized id="lp">_No</Localized></TableCell>
                            <TableCell sx={{ width: 160 }}><Localized id="range">_Range</Localized></TableCell>
                            <TableCell sx={{ textAlign: 'center', width: 120 }}><Localized id="level">_Level</Localized></TableCell>
                            <TableCell><Localized id="description">_Description</Localized></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items && levelsOrder.map((level, index) => {
                            const item = items[level]
                            const style = levelStyles[level]
                            return (
                                <TableRow
                                    key={level}
                                    hover
                                    sx={{
                                        '& td:first-of-type': {
                                            borderLeft: `4px solid ${style.border}`,
                                        },
                                    }}
                                >
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{getDisplayedRange(level, items)}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip
                                            label={level.toUpperCase()}
                                            size="small"
                                            color={style.chipColor}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                '& p': {
                                                    m: 0,
                                                },
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: item?.description ?? '',
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        }

        <LimitsEditDialog
            open={editDialogOpen}
            loading={saving}
            onClose={() => setEditDialogOpen(false)}
            onSave={handleSaveEditDialog}
            initialValues={{
                lowMax: items?.low?.maxValue || 0,
                mediumMax: items?.medium?.maxValue || 0,
                descriptions: {
                    low: items?.low?.description || "",
                    medium: items?.medium?.description || "",
                    high: items?.high?.description || "",
                },
            }}
        />
    </Paper>
}
